import type { MantineTransition } from '@mantine/core';
import type { CSSObject } from 'styled-components';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { Z_INDEX } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';

const { colors } = defaultTheme.v3;

/**
 * Colours the Mantine palette has no shade for. Everything else on these screens resolves to a
 * `theme.v3.colors` shade exactly; only these do not, so only these are named here. `flash` is the one
 * exception: it equals `gray[2]` and is kept here because its role, not its value, is what a reader needs.
 */
export const DS_AI_COLORS = {
	/**
	 * Row flash after a merge (design `--flash`), and the entity equivalent. `flash` is the design's `--line`
	 * grey; it keeps its role name because the keyframe reads as a flash, not as a line.
	 */
	flash: '#E9EBED',
	flashOk: '#DEF5EA',
	/** Pressed state of the primary; `blue[7]` is the hover. */
	primaryPress: '#0A47B8',
	/** Text on the rendered document page (design `--doc-ink`); the UI's ink is `dark[8]`. */
	docInk: '#1B2328',
	errorSoft: '#FDD5DB',
	errorStripe: '#F88796',
	/** Error text (design `--error-text`): the solid error is under 4.5:1 on white, this is 5.5:1. */
	errorText: '#D10C24',
	warningSoft: '#FDE2C2',
	warningStripe: '#F8C58A',
	/** Warning text (design `--warning-text`): 5.5:1 on white, 5.1:1 on the warning tint. */
	warningText: '#A05505',
	successSoft: '#C9EEDF',
	/** The tour's scrim, `dark[8]` at 42% (design `--scrim-strong`). */
	scrimStrong: 'rgba(14, 23, 28, 0.42)',
} as const;

/**
 * Success does not sit in one Mantine family: the tint is `green[0]` and the solid is `teal[8]`,
 * and `green[8]` is a different green altogether. Kept together so the ramp reads as one.
 */
export const DS_AI_SUCCESS = {
	tint: colors.green[0],
	soft: DS_AI_COLORS.successSoft,
	solid: colors.teal[8],
	/** Small success text (design `--success-text`): the solid is 3.25:1 on white, this is 5.6:1. Marks and bars stay `solid`. */
	text: '#00774F',
} as const;

/** The self-hosted document serif; its `@font-face` CSS ships with `documentViewer.styles.ts`. */
export const DS_AI_DOCUMENT_FACE = 'Source Serif 4 Variable';

/**
 * Elevation by role, the design's `--shadow*` layer. The app-wide Mantine `shadows` scale holds bare
 * lengths, so `box-shadow: var(--mantine-shadow-md)` is invalid and renders nothing — never use the
 * `shadow` prop on these screens, use one of these.
 */
export const DS_AI_ELEVATION = {
	/** Popover, tooltip, toast, a floating pill (design `--shadow`). */
	overlay: '0 16px 32px -4px rgba(145, 150, 152, 0.16)',
	/** Menu, the batch list, "Add a panel" (design `--shadow-dropdown`): the same blur with no negative spread. */
	dropdown: '0 16px 32px 0 rgba(145, 150, 152, 0.2)',
	/** Modal, drawer, tour card, the floating dock, the viewer's source list (design `--shadow-lg`). */
	modal: '0 24px 48px -8px rgba(145, 150, 152, 0.2)',
	/** A rendered page or its thumbnail (design `--shadow-paper`): lifted off the grey stage it sits on. */
	paper: '0 1px 3px rgba(14, 23, 28, 0.08), 0 8px 24px -12px rgba(14, 23, 28, 0.18)',
	/**
	 * Edges of a pinned table column, so scrolled content passes under it (design `--shade-left` /
	 * `--shade-right`, set through `--pin`): Review's left-pinned holder column, Analysis's Status column.
	 */
	shadeLeft: '8px 0 10px -8px rgba(14, 23, 28, 0.2)',
	shadeRight: '-8px 0 10px -8px rgba(14, 23, 28, 0.2)',
	/** A switch's knob, lifted off its track. */
	knob: '0 1px 2px rgba(14, 23, 28, 0.2)',
} as const;

/** Proportional figures for a screen root; count-up numbers opt back into tabular with {@link DS_AI_TABULAR_FIGURES}. */
export const DS_AI_PROPORTIONAL_FIGURES = {
	'&, & *': { fontVariantNumeric: 'normal', fontFeatureSettings: 'normal' },
} satisfies CSSObject;

/** The shared focus ring, offset from the element's own outline by `offsetPx`. */
export const dsAiFocusRingStyles = (offsetPx = 0) =>
	({
		'&:focus-visible': {
			outline: `2px solid ${colors.blue[6]}`,
			outlineOffset: `${offsetPx}px`,
		},
	}) satisfies CSSObject;

/** The shared focus ring, drawn on a wrapper when anything inside it takes keyboard focus. */
export const dsAiFocusWithinRingStyles = (offsetPx = 0) =>
	({
		'&:has(:focus-visible)': {
			outline: `2px solid ${colors.blue[6]}`,
			outlineOffset: `${offsetPx}px`,
		},
	}) satisfies CSSObject;

/**
 * The selected fill and its hover, for a row or option that can be picked. A caller that needs its own
 * `&:hover:not(:disabled)` rule must merge into this key, not spread over it.
 */
export const dsAiSelectedFillStyles = (selected: boolean, restBackground = 'transparent') =>
	({
		background: selected ? colors.blue[0] : restBackground,
		'&:hover:not(:disabled)': { background: selected ? colors.blue[0] : colors.gray[0] },
	}) satisfies CSSObject;

/**
 * The design's motion: its three steps (`--t-fast` / `--t` / `--t-slow`), its three curves, and the timings it sets
 * outside the steps. The per-surface names resolve to a step where the design uses one.
 *
 * `easing` is the design's curve for every transition but the overview bar's segment width, and for opacity-only
 * fades; `easeOut` is its curve for entrances that rise, scale or slide; `easeIn` for closing.
 */
export const DS_AI_MOTION = {
	fastMs: 100,
	baseMs: 200,
	slowMs: 300,
	/** The design's `--ease`. */
	easing: 'cubic-bezier(0.2, 0, 0, 1)',
	/** The design's `--ease-out`. */
	easeOut: 'cubic-bezier(0.16, 1, 0.3, 1)',
	/** The design's `--ease-in`. */
	easeIn: 'cubic-bezier(0.4, 0, 1, 1)',

	panelMs: 300,
	panelEasing: 'cubic-bezier(0.2, 0, 0, 1)',
	rowExpandMs: 200,
	drawerMs: 300,
	popoverMs: 200,
	menuMs: 200,
	/**
	 * The design floats a menu or popover in on the CSS `ease-out` keyword, not on its own `--ease-out`
	 * curve; the curve stays for every other entrance.
	 */
	menuEasing: 'ease-out',
	/** A menu or dropdown closing (design `floatOut`). */
	menuExitMs: 100,
	/** A tooltip fading in after `tooltipDelayMs` (design `.tip`). */
	tooltipMs: 100,
	tooltipDelayMs: 100,
	statusChangeMs: 200,
	/** A done mark ticking in (design `tickIn` on a finished stage). */
	tickMs: 300,
	/** Delay between staggered file cards (design `.stagger`). */
	cardStaggerMs: 22,
	/** A list row fading in (design `.row-in`), and the delay between staggered rows. */
	rowEnterMs: 200,
	rowStaggerMs: 16,

	/** A bar segment taking its share of the width (design `.bar-seg` / `.own-bar i`, `--t-slow`). */
	countUpMs: 300,
	/** The overview numbers counting up (design `ms: 700`). */
	overviewCountMs: 700,
	barGrowMs: 300,
	flashMs: 1600,
	/** The viewer's evidence ring pulse (design `hlPulse`), run twice after `pulseDelayMs`. */
	pulseMs: 1100,
	pulseDelayMs: 300,
	/** The upload card's indeterminate fill. The design has no upload fill; this keeps the app's own timing. */
	loadingMs: 1200,
} as const;

/** 1, or 0 under `prefers-reduced-motion`; set on `:root` by `DealstackAiMotionStyles`. */
export const DS_AI_MOTION_VAR = '--ds-ai-motion';

/**
 * A collapsible panel's openness, 1 open and 0 collapsed. `DealstackAiMotionStyles` registers it as a number so it
 * transitions; unregistered, the panel still takes the right width and only the animation is lost.
 */
export const DS_AI_PANE_OPEN_VAR = '--ds-ai-pane-open';

/** Set on a dropdown placed above its target, so it rises into place instead of dropping. */
export const DS_AI_FLOAT_FROM_VAR = '--ds-ai-float-from';

/** Falls back to 0, so a surface rendered without `DealstackAiMotionStyles` gets reduced motion, never full. */
const MOTION_FACTOR = `var(${DS_AI_MOTION_VAR}, 0)`;

/** A distance that is 0 under reduced motion, so a rise or slide becomes a plain fade. */
export const dsAiTravel = (length: string) => `calc(${length} * ${MOTION_FACTOR})`;

/** A scale that stays at 1 under reduced motion. */
export const dsAiScale = (scale: number) => {
	const delta = Math.round((scale - 1) * 1000) / 1000;
	return `calc(1 ${delta < 0 ? '-' : '+'} ${Math.abs(delta)} * ${MOTION_FACTOR})`;
};

/**
 * A duration, or delay, that is 0 under reduced motion: for motion a fade cannot stand in for (a width, a rotation,
 * a zoom, a row growing in, a stagger, a pulse).
 */
export const dsAiMoveMs = (ms: number) => `calc(${ms}ms * ${MOTION_FACTOR})`;

type TransitionStyles = Exclude<MantineTransition, string>;

/**
 * Opens on `enter` and closes on `exit`. Mantine passes one timing function both ways, but the state styles override
 * it, and a CSS transition runs on the curve of the state it moves to.
 */
const withCurves = (shape: TransitionStyles, enter: string, exit: string): TransitionStyles => ({
	...shape,
	in: { ...shape.in, transitionTimingFunction: enter },
	out: { ...shape.out, transitionTimingFunction: exit },
});

/** The design's `popIn` start: 8px down at .985. */
export const DS_AI_POP_FROM = `translateY(${dsAiTravel('8px')}) scale(${dsAiScale(0.985)})`;

/** The design's modal `popIn` / `popOut`; each modal sets its own durations. */
export const modalFadeScale = withCurves(
	{
		in: { opacity: 1, transform: 'none' },
		out: { opacity: 0, transform: DS_AI_POP_FROM },
		transitionProperty: 'opacity, transform',
	},
	DS_AI_MOTION.easeOut,
	DS_AI_MOTION.easeIn
);

/** The design's `drawerIn` / `drawerOut`: 28px from the right with a fade, both together each way. */
export const drawerIn = withCurves(
	{
		in: { opacity: 1, transform: 'none' },
		out: { opacity: 0, transform: `translateX(${dsAiTravel('28px')})` },
		transitionProperty: 'opacity, transform',
	},
	DS_AI_MOTION.easeOut,
	DS_AI_MOTION.easeIn
);

const FLOAT_SHAPE: TransitionStyles = {
	in: { opacity: 1, transform: 'none' },
	out: {
		opacity: 0,
		transform: `translateY(${dsAiTravel(`var(${DS_AI_FLOAT_FROM_VAR}, -4px)`)})`,
	},
	transitionProperty: 'opacity, transform',
};

/** The design's `floatIn` / `floatOut` for menus and popovers: a 4px drop, or a 4px rise when placed above. */
export const floatIn = withCurves(FLOAT_SHAPE, DS_AI_MOTION.menuEasing, DS_AI_MOTION.easeIn);

/** The design's `.batch-pop`: the same 4px float as a plain transition on the standard curve both ways. */
const floatInStandard = withCurves(FLOAT_SHAPE, DS_AI_MOTION.easing, DS_AI_MOTION.easing);

/** The design's `.scrim` for the tour's own overlay: fades in on the standard curve and out on ease-in. */
const scrimFade = withCurves(
	{ in: { opacity: 1 }, out: { opacity: 0 }, transitionProperty: 'opacity' },
	DS_AI_MOTION.easing,
	DS_AI_MOTION.easeIn
);

/**
 * A close with no visible fade. Mantine takes a 0ms exit as "no transition" and then draws the next open unstyled
 * until its transition starts, so the shortest real duration stands in.
 */
export const DS_AI_EXIT_AT_ONCE_MS = 1;

/** Mantine `transitionProps` by surface: each opens on the design's enter timing and closes as its exit does. */
export const DS_AI_TRANSITION_PROPS = {
	/** The design's `.modal`: the small dialogs. */
	modal: {
		transition: modalFadeScale,
		duration: DS_AI_MOTION.popoverMs,
		exitDuration: DS_AI_MOTION.fastMs,
	},
	/** The design's `.big`: the extract dialog and the document viewer. */
	bigModal: {
		transition: modalFadeScale,
		duration: DS_AI_MOTION.slowMs,
		exitDuration: DS_AI_MOTION.baseMs,
	},
	drawer: {
		transition: drawerIn,
		duration: DS_AI_MOTION.drawerMs,
		exitDuration: DS_AI_MOTION.baseMs,
	},
	/** Menus, comboboxes and popovers (design `.menu.float`, `.pop.float`). */
	menu: {
		transition: floatIn,
		duration: DS_AI_MOTION.menuMs,
		exitDuration: DS_AI_MOTION.menuExitMs,
	},
	batchPopover: {
		transition: floatInStandard,
		duration: DS_AI_MOTION.fastMs,
	},
	/** The tour's scrim and card. */
	tour: {
		transition: scrimFade,
		duration: DS_AI_MOTION.popoverMs,
		exitDuration: DS_AI_MOTION.baseMs,
	},
	/** The design's `.tip`: fades in after a short delay, and goes at once. */
	tooltip: {
		transition: 'fade',
		duration: DS_AI_MOTION.tooltipMs,
		enterDelay: DS_AI_MOTION.tooltipDelayMs,
		exitDuration: DS_AI_EXIT_AT_ONCE_MS,
		timingFunction: DS_AI_MOTION.easing,
	},
} as const;

/**
 * A dialog's backdrop, the design's `.scrim`: fades over `baseMs` both ways, in on the standard curve and out on
 * ease-in. Mantine fades an overlay with a single curve, so the curve follows `opened`, which turns false one render
 * before the close starts.
 */
export const dsAiScrimTransition = (opened: boolean) => ({
	duration: DS_AI_MOTION.baseMs,
	exitDuration: DS_AI_MOTION.baseMs,
	timingFunction: opened ? DS_AI_MOTION.easing : DS_AI_MOTION.easeIn,
});

/**
 * Popover props for these screens: the overlay layer, shadow and float. Mantine's `shadow` prop resolves to
 * `var(--mantine-shadow-<key>)`, and the app's scale holds bare lengths, so it renders nothing.
 *
 * The design gives a popover one of three shadows, so there are three sets: a plain popover takes
 * `overlay`, anything that reads as a menu takes `dropdown`, and a popover that reads as a dialog takes
 * `modal`.
 */
export const DS_AI_POPOVER_PROPS = {
	zIndex: Z_INDEX.GLOBAL_MENU,
	transitionProps: DS_AI_TRANSITION_PROPS.menu,
	styles: { dropdown: { boxShadow: DS_AI_ELEVATION.overlay } },
} as const;

/** For a popover that reads as a menu: the batch list (design `.batch-pop`). */
export const DS_AI_POPOVER_DROPDOWN_PROPS = {
	zIndex: Z_INDEX.GLOBAL_MENU,
	transitionProps: DS_AI_TRANSITION_PROPS.menu,
	styles: { dropdown: { boxShadow: DS_AI_ELEVATION.dropdown } },
} as const;

/** For a popover that reads as a dialog: the viewer's source list (design `.src-list`) and the Shortcuts sheet (a `.modal`). */
export const DS_AI_POPOVER_MODAL_PROPS = {
	zIndex: Z_INDEX.GLOBAL_MENU,
	transitionProps: DS_AI_TRANSITION_PROPS.menu,
	styles: { dropdown: { boxShadow: DS_AI_ELEVATION.modal } },
} as const;

/** Tooltip props for these screens: the overlay layer, so a tooltip inside a modal or drawer paints above it. */
export const DS_AI_TOOLTIP_PROPS = {
	zIndex: Z_INDEX.GLOBAL_MENU,
	transitionProps: DS_AI_TRANSITION_PROPS.tooltip,
} as const;

/** Tooltip events for an icon button: the design opens its tooltip on keyboard focus as well as hover. */
export const DS_AI_TOOLTIP_FOCUS_EVENTS = { hover: true, focus: true, touch: false } as const;

/** Tabular figures for a count; the doubled class is load-bearing, so it outranks a screen root's proportional reset. */
export const DS_AI_TABULAR_FIGURES = {
	'&&': { fontVariantNumeric: 'tabular-nums', fontFeatureSettings: "'tnum'" },
} satisfies CSSObject;
